import React, { ComponentType, useEffect } from 'react';
import Head from 'next/head';
import { GetServerSideProps, NextPage } from 'next';
import { CurrentCityContextProvider } from '@cp/entities/location';
import { AppDeclinationProvider } from 'src/app/providers/declination';
import { ClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useDispatch } from 'react-redux';
import { CatalogStateShape, catalogLayoutSlice } from 'src/features/catalog';
import { getCommonProps } from '#app/ssr';
import { ConfigProvider } from '#app/config';
import { CurrentRouteParamsProvider, RouterProvider } from '#app/router';
import { ClientWebRouteResolver } from '#app/routeResolver';
import { ModalProvider } from '#app/modal';
import { CatalogItem, CatalogList } from '#pages/catalog';
import { NotFound } from '#pages/notFound';
import { AppSuggestionsProvider } from '#app/form';
import { Header } from '#widgets/header';
import { Footer } from '#widgets/footer';
import { PageProps } from '#app/page';

const getPageComponent = (routerContextValue: ClientWebRouteParams): ComponentType => {
  switch (routerContextValue.pageId) {
    case ClientWebPage.Catalog:
      return CatalogList;
    case ClientWebPage.CatalogItem:
      return CatalogItem;
    default:
      return NotFound;
  }
};

interface CatalogPageProps extends PageProps<CatalogStateShape> {
  state: CatalogStateShape;
}

const Page: NextPage<CatalogPageProps> = ({ config, routerContextValue, state }) => {
  const Component = getPageComponent(routerContextValue);
  const dispatch = useDispatch();

  useEffect(() => {
    // partially sync server side state with client
    dispatch(catalogLayoutSlice.actions.setActiveItem(state.catalog.activeItemId));
  }, [state.catalog.activeItemId, dispatch]);

  return (
    <ConfigProvider config={config}>
      <Head>
        <meta content="width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no" name="viewport" />
      </Head>
      <RouterProvider>
        <ClientWebRouteResolver>
          <CurrentRouteParamsProvider routerContextValue={routerContextValue}>
            <CurrentCityContextProvider>
              <AppDeclinationProvider>
                <AppSuggestionsProvider>
                  <ModalProvider>
                    <Header sticky />
                    <Component />
                    <Footer />
                  </ModalProvider>
                </AppSuggestionsProvider>
              </AppDeclinationProvider>
            </CurrentCityContextProvider>
          </CurrentRouteParamsProvider>
        </ClientWebRouteResolver>
      </RouterProvider>
    </ConfigProvider>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }) => {
  const props = await getCommonProps({ req, res, query });
  return { props };
};
